/**
 * CER-58. Approving a report that has non-exempt checklist items with no photo
 * takes an explicit acknowledgement and a reason. The checkbox and the reason
 * are both required; closing the dialog leaves the report unapproved.
 */

import { useState } from 'react';
import type { MissingClausePhoto, QaReport } from '@cer-autofill/shared';
import { formatMissingClausePhoto, MISSING_PHOTO_EXEMPT_LABELS } from '@cer-autofill/shared';

export function missingPhotoOverrideReady(acknowledged: boolean, reason: string): boolean {
  return acknowledged && reason.trim().length > 0;
}

export function MissingPhotoOverrideDialog({
  items,
  report,
  busy,
  onCancel,
  onConfirm,
}: {
  items: readonly MissingClausePhoto[];
  report: QaReport;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState('');
  const ready = missingPhotoOverrideReady(acknowledged, reason);
  const otherFindings = report.results.filter(
    (result) => result.ruleId !== 'missing-clause-photos' && result.status !== 'pass',
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="missing-photo-title">
      <div className="modal__card modal__card--wide">
        <h3 id="missing-photo-title">Approve without these photos?</h3>
        <p>
          These checklist items have no photo. {MISSING_PHOTO_EXEMPT_LABELS.join(', ')} items are
          not listed. Approving records your name, the time and the reason on the job.
        </p>
        <ul className="missing-photo-list">
          {items.map((item) => (
            <li key={`${item.section}:${item.clauseId}`}>{formatMissingClausePhoto(item)}</li>
          ))}
        </ul>
        {otherFindings.length > 0 && (
          <p className="missing-photo-other">
            This approval also overrides {otherFindings.map((result) => result.label).join(', ')}.
          </p>
        )}
        <label className="missing-photo-ack">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>I acknowledge these items have no photo</span>
        </label>
        <label>
          <span>Reason</span>
          <textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is it acceptable to approve without these photos?"
            required
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !ready}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? 'Approving…' : 'Acknowledge and approve'}
          </button>
        </div>
      </div>
    </div>
  );
}
